'use client';

import {
  Breakpoint,
  Button,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuRoot,
  DropdownMenuTrigger,
  IconButton,
  SheetBody,
  SheetClose,
  SheetContent,
  SheetHeader,
  SheetHeading,
  SheetRoot,
  SheetTrigger,
  Stack,
} from '@pie/amber';
import { IconEllipsisVertical } from '@pie/amber-icons';
import NextLink from 'next/link';
import { useState } from 'react';
import { StandardCoiDialog } from '@/components/StandardCoiDialog';
import { env } from '@/config/env';
import { isActivePolicy } from '@/hooks/usePolicy';
import { IssuedSearchPolicy } from '@/hooks/useSearchPolicies';

type Props = {
  policy: IssuedSearchPolicy;
};

const MobilePolicyActionsSheet = ({ policy }: Props) => {
  const [dialog, setDialog] = useState<null | 'actions' | 'coi'>(null);

  return (
    <>
      <SheetRoot
        onOpenChange={(next) => {
          setDialog(next === true ? 'actions' : null);
        }}
        open={dialog === 'actions'}
        side="bottom"
      >
        <SheetTrigger>
          <IconButton
            emphasis="light"
            label={`Actions for ${policy.lastSegment.primaryLegalEntity.entityName}`}
            size="sm"
            title="More Actions"
            tone="accent"
          >
            <IconEllipsisVertical size="sm" />
          </IconButton>
        </SheetTrigger>
        <SheetContent closeOnOutsideInteraction>
          <SheetHeader>
            <SheetHeading>Policy Actions</SheetHeading>
            <SheetClose />
          </SheetHeader>
          <SheetBody>
            <Stack gap="layout-2xs">
              <Button
                as="a"
                // TODO: the mocks ignore line-height, so must customize py-
                className="px-xs py-[4px] w-full"
                emphasis="light"
                href={env.NEXT_PUBLIC_FILE_CLAIM_URL}
                rel="noopener noreferrer"
                size="lg"
                target="_blank"
                tone="neutral"
                weight="body"
              >
                File a Claim
              </Button>
              <Button
                as={NextLink}
                // TODO: the mocks ignore line-height, so must customize py-
                className="px-xs py-[4px] w-full"
                emphasis="light"
                href={`/billing?policy=${policy.policyId}`}
                size="lg"
                tone="neutral"
                weight="body"
              >
                View Payment Activity
              </Button>
              <Button
                as={NextLink}
                // TODO: the mocks ignore line-height, so must customize py-
                className="px-xs py-[4px] w-full"
                emphasis="light"
                href={`/policy/${policy.policyId}/documents`}
                size="lg"
                tone="neutral"
                weight="body"
              >
                View Policy Documents
              </Button>

              {isActivePolicy(policy) && (
                <Button
                  // TODO: the mocks ignore line-height, so must customize py-
                  className="px-xs py-[4px] w-full"
                  emphasis="light"
                  onClick={() => {
                    setDialog('coi');
                  }}
                  size="lg"
                  tone="neutral"
                  weight="body"
                >
                  Create Certificate of Insurance
                </Button>
              )}
            </Stack>
          </SheetBody>
        </SheetContent>
      </SheetRoot>
      <StandardCoiDialog
        onClose={() => setDialog(null)}
        open={dialog === 'coi'}
        policyId={policy.policyId}
      />
    </>
  );
};

export const PolicyActionsMenu = ({ policy }: Props) => {
  const [dialog, setDialog] = useState<null | 'coi'>(null);

  return (
    <Breakpoint above="sm" fallback={<MobilePolicyActionsSheet policy={policy} />}>
      <DropdownMenuRoot>
        <DropdownMenuTrigger>
          <IconButton
            emphasis="light"
            label={`Actions for ${policy.lastSegment.primaryLegalEntity.entityName}`}
            size="sm"
            title="More Actions"
            tone="accent"
          >
            <IconEllipsisVertical size="sm" />
          </IconButton>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" side="bottom">
          <DropdownMenuItem
            as="a"
            href={env.NEXT_PUBLIC_FILE_CLAIM_URL}
            rel="noopener noreferrer"
            target="_blank"
          >
            File a Claim
          </DropdownMenuItem>
          <DropdownMenuItem as={NextLink} href={`/billing?policy=${policy.policyId}`}>
            View Payment Activity
          </DropdownMenuItem>
          <DropdownMenuItem as={NextLink} href={`/policy/${policy.policyId}/documents`}>
            View Policy Documents
          </DropdownMenuItem>
          {isActivePolicy(policy) && (
            <DropdownMenuItem
              onClick={() => {
                setDialog('coi');
              }}
            >
              Create Certificate of Insurance
            </DropdownMenuItem>
          )}
        </DropdownMenuContent>
      </DropdownMenuRoot>
      <StandardCoiDialog
        onClose={() => setDialog(null)}
        open={dialog === 'coi'}
        policyId={policy.policyId}
      />
    </Breakpoint>
  );
};
